import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useCallback, useMemo } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { axisClasses, barClasses, barLabelClasses, BarPlot, ChartsContainer, ChartsGrid, ChartsXAxis, ChartsYAxis, LinePlot, MarkElement, MarkElementProps, MarkPlot } from "@mui/x-charts";
import { BarProps } from "@mui/x-charts/BarChart/AnimatedBarElement";
import { ObraChecklistServicoEstatisticas } from "../../../../domain/models/obra_checklist_servico_estatiticas";

/**
 * CustomBarLabelElement
 */

interface CustomBarProps extends BarProps {
  colors: string[];
}

const CustomBar = ({
  id,
  dataIndex,
  colors,
  ownerState,
  skipAnimation,
  xOrigin,
  yOrigin,
  ...props
}: CustomBarProps) => {
  return (
    <rect {...props} color={colors[dataIndex]} fill={colors[dataIndex]} />
  );
}

/**
 * CustomMarkWithLabel
 */

interface CustomMarkElementProps extends MarkElementProps {
  size: number;
  dataset: any[];
};

export const CustomMarkElement = (props: CustomMarkElementProps) => {
  const { x, y, size, dataIndex, dataset } = props;

  const value = dataset[dataIndex].meta;

  return (
    <g>
      <MarkElement
        {...props}
      />

      <text
        x={x}
        y={Number(y) - (size / 2) - 7}
        fill='white'
        textAnchor="middle"
        style={{
          fontSize: '18px',
          paintOrder: 'stroke',
          stroke: '#000000',
          strokeWidth: '2px',
          strokeLinecap: 'butt',
          strokeLinejoin: 'miter',
          fontWeight: 800,
        }}>
        {value}
      </text>
    </g>
  );
};

/**
 * ObraChecklistServicoGraficoModal
 */

type Props = {
  open: boolean;
  loading: boolean;
  estatisticasChecklistServico: ObraChecklistServicoEstatisticas | null;
  onClose: () => void;
};

export const ObraChecklistServicoGraficoModal = ({
  open,
  loading,
  estatisticasChecklistServico,
  onClose,
}: Props) => {
  const maxValue = useMemo(() => {
    if (!estatisticasChecklistServico) {
      return 0;
    }
    if (estatisticasChecklistServico.percentual === null || estatisticasChecklistServico.total === null) {
      return 0;
    }
    const needMoreOneLimit =
      estatisticasChecklistServico.percentual === 100 ||
      estatisticasChecklistServico.total === estatisticasChecklistServico.totalA ||
      estatisticasChecklistServico.total === estatisticasChecklistServico.totalR ||
      estatisticasChecklistServico.total === estatisticasChecklistServico.totalRA ||
      estatisticasChecklistServico.total === estatisticasChecklistServico.totalX ||
      estatisticasChecklistServico.total === estatisticasChecklistServico.totalNA;

    if (needMoreOneLimit) {
      return estatisticasChecklistServico.total + (estatisticasChecklistServico.total * 0.10);
    }
    return estatisticasChecklistServico.total;
  }, [
    estatisticasChecklistServico,
  ]);

  const dataset = useMemo(() => {
    if (estatisticasChecklistServico === null) {
      return [];
    }
    if (estatisticasChecklistServico.percentual === null || estatisticasChecklistServico.total === null) {
      return [];
    }
    const meta = (estatisticasChecklistServico.percentual / 100) * estatisticasChecklistServico.total;
    return [
      {
        meta: Number(meta.toFixed(2)),
        quantidade: estatisticasChecklistServico.totalA,
        period: "Total de Aprovações (A)",
      },
      {
        meta: Number(meta.toFixed(2)),
        quantidade: estatisticasChecklistServico.totalR,
        period: "Total de Retrabalhos (R)"
      },
      {
        meta: Number(meta.toFixed(2)),
        quantidade: estatisticasChecklistServico.totalX,
        period: "Total de Reprovações (X)"
      },
      {
        meta: Number(meta.toFixed(2)),
        quantidade: estatisticasChecklistServico.totalRA,
        period: "Total de Reaprovações (RA)"
      },
      {
        meta: Number(meta.toFixed(2)),
        quantidade: estatisticasChecklistServico.totalNA,
        period: "Não se aplica (NA)"
      }
    ];
  }, [
    estatisticasChecklistServico,
  ]);

  const colors = useMemo(() => {
    return ['#26c281', '#ffff00', '#fb3113', '#ff8000', '#8ea3df', 'white'];
  }, [
  ]);

  const xAxis = useMemo((): any => {
    return [
      { scaleType: "band", dataKey: 'period' },
    ];
  }, [
  ]);

  const series = useMemo((): any => {
    return [
      { id: 1, type: 'bar', dataKey: 'quantidade', barLabel: 'value' },
      { id: 2, type: 'line', dataKey: 'meta', barLabel: 'value' },
    ];
  }, [
  ]);

  const chartSetting = useMemo(() => ({
    sx: {
      [`& .${axisClasses.directionY} .${axisClasses.label}`]: {
        transform: 'translateX(-10px)',
      },
      [`& .${axisClasses.directionX} .${axisClasses.label}`]: {
        transform: 'translateY(-10px)',
      },
    },
  }), [
  ]);

  const handleClose = useCallback(() => {
    if (!loading) onClose();
  }, [
    loading,
    onClose,
  ]);

  return (
    <Dialog
      open={open}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "70rem",
          },
        },
      }}>
      <DialogTitle>
        Gráfico Resumo
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={loading} message="Carregando...">
          <div className="w-full h-[30rem]">
            <ChartsContainer
              dataset={dataset}
              xAxis={xAxis}
              yAxis={[{ min: 0, max: maxValue }]}
              series={series}
              {...chartSetting}
              sx={{
                [`& .${barClasses.root} .${barClasses.seriesLabels} .${barLabelClasses.root}`]: {
                  fill: 'white',
                  fontSize: '18px',
                  paintOrder: 'stroke',
                  stroke: '#000000',
                  strokeWidth: '2px',
                  strokeLinecap: 'butt',
                  strokeLinejoin: 'miter',
                  fontWeight: 800,
                },
                '& .MuiMarkElement-root': {
                  fill: '#a5e900',
                  stroke: '#00c000',
                },
              }}>

              <ChartsGrid horizontal={true} />

              <BarPlot
                slots={{ bar: CustomBar as any }}
                slotProps={{ bar: { colors } as any }}
              />

              <LinePlot
                slots={{
                  line: ({ skipAnimation, ownerState, ...props }) => <path {...props} stroke="#00c000" />
                }}
              />

              <MarkPlot
                slots={{
                  mark: props => (
                    <CustomMarkElement
                      size={8}
                      dataset={dataset}
                      {...props}
                    />
                  )
                }}
              />

              <ChartsXAxis />
              <ChartsYAxis />
            </ChartsContainer>
          </div>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-end flex p-4 gap-4">
          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Fechar
              </h1>
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog >
  );
}